// ─── Deal timing ──────────────────────────────────────────────────────────
// Derives time_to_loi_days + time_in_uw_days for a deal by looking at
// activity_log's stage_changed events. Called after every stage-change
// path (App.handleStageChange, App.handleKillSaved, DealRoom.changeStage)
// so the two integers on the deal row stay current.
//
// Definitions:
//   time_to_loi_days = days between deal creation and the FIRST transition
//                      into "LOI Submitted"
//   time_in_uw_days  = days between the FIRST transition into "Underwriting"
//                      and the NEXT transition out of it (which may be into
//                      LOI Submitted, killed, etc.). If the deal is still in
//                      UW, we anchor on now(). If the deal is currently
//                      killed but no exit event exists, we anchor on
//                      killed_at.
//
// Both compute to null when the required events aren't present, and we
// leave the column alone rather than overwriting a good value with null.

import { supabase } from "../supabase";
import { collapseSlips } from "./stageLog";

const DAY_MS = 86_400_000;

function daysBetween(a, b) {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round(ms / DAY_MS);
}

// Returns { time_to_loi_days, time_in_uw_days } (values may be null).
// Deal shape expected: { id, createdAt, killedAt, stage }. createdAt should
// be an ISO timestamp string.
export async function computeDealTiming(deal) {
  if (!deal?.id) return { time_to_loi_days: null, time_in_uw_days: null };

  const { data, error } = await supabase
    .from("activity_log")
    .select("entity_id, action, metadata, created_at")
    .eq("entity_id", deal.id)
    .eq("entity_type", "deal")
    .eq("action", "stage_changed")
    .order("created_at", { ascending: true });
  if (error) {
    console.warn("computeDealTiming: activity_log read failed:", error.message);
    return { time_to_loi_days: null, time_in_uw_days: null };
  }

  const events = collapseSlips(data || []);
  const firstEnterLOI = events.find(e => e.metadata?.new_stage === "LOI Submitted");
  const firstEnterUW  = events.find(e => e.metadata?.new_stage === "Underwriting");
  const firstExitUW   = firstEnterUW
    ? events.find(e => new Date(e.created_at) > new Date(firstEnterUW.created_at)
                    && e.metadata?.old_stage === "Underwriting")
    : null;

  const time_to_loi_days = firstEnterLOI
    ? daysBetween(deal.createdAt, firstEnterLOI.created_at)
    : null;

  // Anchor UW exit — the next stage_changed after entering UW; failing that,
  // killed_at (if the deal was killed while still in UW); failing that, now().
  let uwExitAt = firstExitUW?.created_at;
  if (!uwExitAt && deal.killedAt && deal.stage !== "Underwriting") uwExitAt = deal.killedAt;
  if (!uwExitAt && deal.stage === "Underwriting") uwExitAt = new Date().toISOString();
  const time_in_uw_days = firstEnterUW ? daysBetween(firstEnterUW.created_at, uwExitAt) : null;

  return { time_to_loi_days, time_in_uw_days };
}

// Recompute + persist. Fire-and-forget from any stage-change path — errors
// are swallowed with a warn so a timing hiccup doesn't block the user's
// primary action.
export async function refreshDealTiming(deal) {
  try {
    const timing = await computeDealTiming(deal);
    // Skip the write if both values are null — nothing to persist and we
    // don't want to overwrite a previously-computed value.
    if (timing.time_to_loi_days == null && timing.time_in_uw_days == null) return;
    await supabase.from("deals").update({
      time_to_loi_days: timing.time_to_loi_days,
      time_in_uw_days: timing.time_in_uw_days,
    }).eq("id", deal.id);
  } catch (e) {
    console.warn("refreshDealTiming failed:", e.message);
  }
}
